'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

export type ActionsMenuItem = {
  key: string;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  disabled?: boolean;
};

const VARIANT_CLASSES: Record<NonNullable<ActionsMenuItem['variant']>, string> = {
  default: 'text-slate-700 hover:bg-bg',
  danger: 'text-red-600 hover:bg-red-50',
  warning: 'text-amber-600 hover:bg-amber-50',
  success: 'text-emerald-600 hover:bg-emerald-50',
};

const MENU_WIDTH = 176;

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
}

export function ActionsMenu({
  items,
  label = 'Actions',
  buttonClassName,
  menuClassName,
  menuWidth = MENU_WIDTH,
}: {
  items: ActionsMenuItem[];
  label?: string;
  buttonClassName?: string;
  menuClassName?: string;
  menuWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mounted = useMounted();

  const updatePosition = () => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? items.length * 36 + 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;
    const top = openUp ? rect.top - menuHeight - 4 : rect.bottom + 4;
    const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);

    setMenuStyle({
      position: 'fixed',
      top,
      left,
      width: menuWidth,
      zIndex: 9999,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, items.length]);

  const menu =
    open && mounted ? (
      <div
        ref={menuRef}
        role="menu"
        style={menuStyle}
        className={`rounded-xl border border-nature-border/80 bg-bg p-1 shadow-[0_12px_40px_-12px_rgba(45,53,54,0.28)] ${menuClassName ?? ''}`}
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              setOpen(false);
              item.onClick();
            }}
            className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors disabled:opacity-40 ${
              VARIANT_CLASSES[item.variant ?? 'default']
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border border-nature-border text-slate-500 hover:bg-bg hover:text-slate-700 ${buttonClassName ?? ''}`}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}
